import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { includesQuery } from "../../Organization/CodeSecurity/codeSecurityShared";
import { styles } from "../styles/publicStyles";
import { formatLocations } from "../data/subprocessors";
import VendorLogo from "./VendorLogo";
import EmptySection, { companyName } from "./EmptySection";
import ShowMoreButton from "./ShowMoreButton";
import { usePagedList } from "../hooks/usePagedList";
import { useFocusFlash } from "../hooks/useFocusFlash";

// One vendor: logo, "Name • what they do", and where it stores data on the
// right (under the name on phones) when that is known.
const SubprocessorRow = ({ row, focus }) => {
  const theme = useTheme();
  const places = formatLocations(row.dataLocations);
  const { ref, highlighted } = useFocusFlash(focus);
  return (
    <Stack
      ref={ref}
      direction="row"
      spacing={2}
      alignItems="center"
      aria-current={highlighted ? "true" : undefined}
      sx={{
        px: 3,
        py: 2.5,
        backgroundColor: highlighted ? theme.trust.secondarySoft : "transparent",
        transition: "background-color 600ms ease",
        "&:not(:first-of-type)": {
          borderTop: `1px solid ${theme.customColors.border.main}`,
        },
      }}
    >
      <VendorLogo name={row.name} logoPath={row.logoPath} size={40} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 2 }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Typography sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ fontWeight: 500 }}>
            {row.name}
          </Box>
          {row.purpose && (
            <Box
              component="span"
              sx={{ color: theme.customColors.text.secondary }}
            >
              {" "}
              • {row.purpose}
            </Box>
          )}
        </Typography>
        {/* Shown only when the company has said where. */}
        {places && (
          <Typography
            variant="body2"
            sx={{ flexShrink: 0, textAlign: { sm: "right" } }}
          >
            {places}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

// Subprocessors tab, laid out like Vanta's and like our Resources tab: the
// title and search on one line, then one list of vendors. With none listed
// the tab stays and says so. Shown a page at a time; `focus` ({ id, at }) is
// the vendor the global search picked, shown even if on a later page.
const SubprocessorsSection = ({ subprocessors, company, focus }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const none = subprocessors.length === 0;

  const rows = useMemo(
    () =>
      subprocessors.filter((s) =>
        includesQuery(
          search,
          s.name,
          s.purpose,
          formatLocations(s.dataLocations)
        )
      ),
    [subprocessors, search]
  );
  const { shown, remaining, showMore, reveal, reset } = usePagedList(rows);

  useEffect(() => {
    if (!focus) return;
    setSearch("");
    reveal(subprocessors.findIndex((s) => s.id === focus.id));
  }, [focus, subprocessors, reveal]);

  return (
    <Box id="subprocessors">
      {!none && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              reset();
            }}
            inputProps={{ "aria-label": "Search subprocessors" }}
            sx={{ width: { xs: "100%", md: 300 }, backgroundColor: "#FFFFFF" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      )}
      {none ? (
        <EmptySection
          icon={BusinessOutlinedIcon}
          title="No subprocessors listed yet"
          message={`${companyName(company)} has not listed the companies that process customer data on its behalf.`}
          company={company}
          askText="To ask about them"
        />
      ) : (
        <>
        <Box
          sx={{
            ...styles.mainCard,
            overflow: "hidden",
            backgroundColor: theme.customColors.background.main,
          }}
        >
          {rows.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                p: 3,
                textAlign: "center",
                color: theme.customColors.text.secondary,
              }}
            >
              No subprocessors match your search.
            </Typography>
          ) : (
            shown.map((row) => (
              <SubprocessorRow
                key={row.id}
                row={row}
                focus={focus?.id === row.id ? focus : null}
              />
            ))
          )}
        </Box>
        <ShowMoreButton
          remaining={remaining}
          label="Show more subprocessors"
          onClick={showMore}
        />
        </>
      )}
    </Box>
  );
};

export default SubprocessorsSection;
